import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  AccordionDetails,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import clsx from 'clsx';
import React, { useMemo, useState } from 'react';
import { IconStack } from '../Header/styles';
import { SubmenuBlock } from '../SubmenuBlock';
import type { ServicesSubmenuType } from './ServicesSubmenu.types';
import { servicesSubmenuData } from './servicesSubmenuData';
import {
  CategoriesWrapper,
  ServicesWrapper,
  StyledAccordion,
  StyledAccordionStack,
  StyledAccordionSummary,
  StyledCategoryItem,
  StyledStack,
} from './styles';

const DesktopLayout = () => {
  const [categoryIndex, setCategoryIndex] = useState(0);

  const services = useMemo(() => {
    return servicesSubmenuData[categoryIndex].services;
  }, [categoryIndex]);

  return (
    <Stack direction="row" sx={{ width: '100%' }}>
      <CategoriesWrapper>
        {servicesSubmenuData.map(({ category }, index) => {
          const isSelected = categoryIndex === index;
          return (
            <StyledCategoryItem
              variant="strong_300"
              isSelected={isSelected}
              key={index}
              onClick={() => setCategoryIndex(index)}>
              {category}
            </StyledCategoryItem>
          );
        })}
      </CategoriesWrapper>

      <ServicesWrapper>
        {services.map((block, index) => (
          <SubmenuBlock
            type="icon"
            key={index}
            iconPrefix={'services'}
            {...block}
          />
        ))}
      </ServicesWrapper>
    </Stack>
  );
};

const MobileLayout = () => {
  const theme = useTheme();
  const [categoryIndex, setCategoryIndex] = useState<number | null>(null);

  return (
    <StyledAccordionStack>
      {servicesSubmenuData.map(({ category, services }, index) => {
        const isSelected = categoryIndex === index;

        return (
          <StyledAccordion
            key={index}
            elevation={0}
            disableGutters
            square
            expanded={isSelected}
            onChange={() => {
              if (isSelected) {
                setCategoryIndex(null);
                return;
              }
              setCategoryIndex(index);
            }}>
            <StyledAccordionSummary
              className={clsx(isSelected ? 'Opaque' : 'Transparent')}
              isSelected={isSelected}
              expandIcon={
                <IconStack display={'flex'}>
                  <AddIcon className={!isSelected ? 'ShowIcon' : 'HideIcon'} />
                  <RemoveIcon
                    className={isSelected ? 'ShowIcon' : 'HideIcon'}
                  />
                </IconStack>
              }>
              <StyledCategoryItem variant="strong_300" isSelected={isSelected}>
                {category}
              </StyledCategoryItem>
            </StyledAccordionSummary>

            <AccordionDetails>
              <ServicesWrapper>
                {services.map((block, index) => (
                  <SubmenuBlock
                    type="icon"
                    key={index}
                    color={theme.palette.invertedContent?.SECONDARY}
                    {...block}
                    iconPrefix={'services'}
                  />
                ))}
              </ServicesWrapper>
            </AccordionDetails>
          </StyledAccordion>
        );
      })}
    </StyledAccordionStack>
  );
};

export const ServicesSubmenu = (
  props: ServicesSubmenuType
): React.ReactElement => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <StyledStack {...props}>
      {isMobile ? <MobileLayout /> : <DesktopLayout />}
    </StyledStack>
  );
};
